import axios from "axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchError } from "./commonSlice";

// Vendor quality rating colours, exactly as specified by the business:
//   1, 2 -> red, 3 -> yellow, 4 -> orange, 5 -> dark green.
// 1 and 2 deliberately share one red - the scale has five steps but only four
// colours. Defined once here so the vendor screens and the PO screens can
// never drift apart; hex rather than MUI palette names because these are
// fixed brand values, not theme colours.
export const VENDOR_RATING_COLORS = {
  1: "#901118",
  2: "#901118",
  3: "#E5D606",
  4: "#DB4F11",
  5: "#0A8F3E",
};

// Unrated vendors deliberately fall through to the default text colour.
export const getVendorRatingColor = (rating) =>
  VENDOR_RATING_COLORS[rating] || "inherit";

const initialState = {
  list: [],
  perPage: "",
  total: 0,
  loading: false,
};

export const getSupplierList = createAsyncThunk(
  "get-supplier",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("supplier/list", { params: data });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// Same endpoint as the list, for the Excel download: the caller asks for every
// row the current filters match, in one page, instead of the page on screen.
//
// Deliberately has no reducer case - the rows it returns are written straight
// into the file and must never land in `state.list`, or the table on screen
// would jump from its page size to the whole filtered set.
export const exportSupplierList = createAsyncThunk(
  "export-supplier",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("supplier/list", { params: data });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const createSupplier = createAsyncThunk(
  "create-supplier",
  async (data, { dispatch }) => {
    try {
      const response = await axios.post("supplier", data);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const editSupplier = createAsyncThunk(
  "edit-supplier",
  async (id, { dispatch }) => {
    try {
      const response = await axios.get(`supplier/${id}`);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const updateSupplier = createAsyncThunk(
  "update-supplier",
  async ({ id, data }, { dispatch }) => {
    try {
      const response = await axios.put(`/supplier/${id}`, data);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);
export const managePriceSupplier = createAsyncThunk(
  "update-supplier",
  async (data, { dispatch }) => {
    try {
      const response = await axios.put(`/supplier/manage-price`, data);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const deleteSupplier = createAsyncThunk(
  "delete-supplier",
  async (id, { dispatch }) => {
    try {
      const response = await axios.delete(`/supplier/${id}`);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// Vendor activity: has this vendor ever logged in, what have they looked at,
// and what is in their cart right now.
export const getSupplierActivity = createAsyncThunk(
  "get-supplier-activity",
  async (id, { dispatch }) => {
    try {
      const response = await axios.get(`supplier/${id}/activity`);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

export const getSupplierProductViews = createAsyncThunk(
  "get-supplier-product-views",
  async ({ id, ...params }, { dispatch }) => {
    try {
      const response = await axios.get(`supplier/${id}/product-views`, {
        params,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

export const approveSupplier = createAsyncThunk(
  "approve-supplier",
  async ({ id, userId }, { dispatch }) => {
    try {
      const response = await axios.patch(`/supplier/${id}/approve`, { userId });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const rejectSupplier = createAsyncThunk(
  "reject-supplier",
  async ({ id, reason }, { dispatch }) => {
    try {
      const response = await axios.patch(`/supplier/${id}/reject`, { reason });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const deleteSupplierContact = createAsyncThunk(
  "delete-contact",
  async (id, { dispatch }) => {
    try {
      const response = await axios.delete(`/supplier/contact/${id}`);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

const supplierSlice = createSlice({
  name: "supplier",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getSupplierList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSupplierList.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action?.payload?.data?.data?.supplierList?? [];
        state.perPage = action?.payload?.data?.data?.perPage?? 1;
        state.total = action?.payload?.data?.data?.total?? 0;
      })
      .addCase(getSupplierList.rejected, (state, action) => {
        state.loading = false;
        state.list = "";
        state.perPage = "";
        state.total = "";
      });
  },
});

export default supplierSlice.reducer;
